import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowDown, RefreshCw } from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";

export default function Roadmap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { format, currency } = useCurrency();
  // The roadmap salary is expressed in the user's resolved reporting currency;
  // if the backend later returns a `currency` field we prefer that.
  const fmt = (n) => (n ? format(n, { currency: data?.currency || currency }) : "—");

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/roadmap");
      setData(r.data.data);
    } catch { toast.error("Complete your profile first"); } finally { setLoading(false); }
  };
  const refresh = async () => {
    setRefreshing(true);
    try { const r = await api.post("/roadmap/refresh"); setData(r.data.data); toast.success("Roadmap regenerated"); }
    catch { toast.error("Refresh failed"); } finally { setRefreshing(false); }
  };

  if (loading) return <div className="p-10">Generating your roadmap...</div>;
  if (!data) return (
    <div className="p-10">
      <h1 className="font-display font-bold text-4xl mb-4">Career Roadmap</h1>
      <button data-testid="roadmap-generate" onClick={refresh} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors px-6 py-2.5 mt-6">Generate roadmap</button>
    </div>
  );

  return (
    <div className="p-10 max-w-5xl" data-testid="roadmap-page">
      <div className="flex justify-between items-start mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Module 03</div>
          <h1 className="font-display font-bold text-5xl tracking-tight">Career Roadmap</h1>
          <div className="text-muted-foreground mt-4 max-w-2xl">
            From <span className="text-foreground font-medium">{data.current_role}</span> → <span className="text-primary font-medium">{data.north_star}</span>
          </div>
        </div>
        <button data-testid="roadmap-refresh" onClick={refresh} disabled={refreshing} className="inline-flex items-center justify-center rounded-md border border-border bg-background hover:bg-accent text-foreground transition-colors px-4 py-2 text-sm flex items-center gap-2"><RefreshCw size={14} className={refreshing?"animate-spin":""}/> {refreshing?"Refreshing":"Refresh"}</button>
      </div>

      <div className="space-y-3">
        {(data.steps || []).map((step, i) => (
          <div key={i}>
            <div className="card-flat p-6 grid md:grid-cols-[1fr_auto] gap-6" data-testid={`roadmap-step-${i}`}>
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="font-mono text-xs px-2 py-0.5 bg-primary text-foreground">STEP {String(i+1).padStart(2,"0")}</div>
                  <div className={`text-xs font-mono uppercase tracking-wider ${step.likelihood==="High"?"text-emerald-400":step.likelihood==="Medium"?"text-amber-400":"text-red-400"}`}>{step.likelihood} likelihood</div>
                </div>
                <div className="font-display font-bold text-2xl tracking-tight">{step.role}</div>
                <div className="text-sm text-muted-foreground mt-2">{step.rationale}</div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">Required skills</div>
                    <div className="text-xs text-foreground/80">{(step.required_skills||[]).join(" · ") || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">Certifications</div>
                    <div className="text-xs text-foreground/80">{(step.required_certifications||[]).join(" · ") || "—"}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">Suggested learning</div>
                    <div className="text-xs text-foreground/80">{(step.suggested_learning||[]).join(" · ") || "—"}</div>
                  </div>
                </div>
              </div>
              <div className="border-l md:border-l border-border md:pl-6 flex flex-col justify-center">
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Salary</div>
                <div className="font-display font-black text-3xl tracking-tight">{fmt(step.salary)}</div>
                <div className="text-xs font-mono text-muted-foreground mt-2">~{step.years_to_reach} yrs</div>
              </div>
            </div>
            {i < data.steps.length - 1 && (
              <div className="flex justify-center py-2 text-muted-foreground/60"><ArrowDown size={18}/></div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
